import {
  Autocomplete,
  Box,
  Divider,
  IconButton,
  TextField,
  Typography,
  createFilterOptions,
} from "@mui/material";
import useLanguage from "../../hooks/useLanguage";
import useContent from "../../hooks/useContent";
import styled from "styled-components";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { useEffect, useState } from "react";

// Cap the rendered dropdown; the full vocabulary is ~7252 items.
const tagFilter = createFilterOptions({ limit: 20 });
// Matches the auto-tagger's 3-5 range — never more than 5 tags per article.
const MAX_TAGS = 5;

const DrawerWrapper = styled(Box)`
  padding: 16px;
  display: flex;
  flex-direction: column;
  height: 100%;
  gap: 8px;
`;

const TitleWrapper = styled(Box)`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Image = styled("img")`
  width: 100%;
  aspect-ratio: 4/3;
  border-radius: ${({ theme }) => theme.shape.borderRadius};
  object-fit: cover;
  border: 1px solid ${({ theme }) => theme.palette.divider};
`;

export default function EditorDrawer({ form, onChange, onClose }) {
  const [metadata, setMetadata] = useState(form);
  const [vocab, setVocab] = useState([]);
  const { getTagVocabulary } = useContent();
  const [
    {
      pages: {
        editor: { drawer: text },
      },
    },
  ] = useLanguage();

  useEffect(() => {
    getTagVocabulary()
      .then((res) => setVocab(res?.tags || []))
      .catch(() => setVocab([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "banner")
      setMetadata((prev) => ({
        ...prev,
        metadata: { ...prev.metadata, banner: value },
      }));
    else if (name === "description")
      setMetadata((prev) => ({
        ...prev,
        metadata: { ...prev.metadata, description: value },
      }));
    else setMetadata((prev) => ({ ...prev, [name]: value }));
  };

  useEffect(() => {
    onChange(metadata);
  }, [metadata]);

  useEffect(() => {
    setMetadata(form);
  }, [form]);

  return (
    <DrawerWrapper>
      <TitleWrapper>
        <Typography variant="h5">{text.title}</Typography>
        <IconButton onClick={onClose} size="small">
          <CloseRoundedIcon fontSize="small" />
        </IconButton>
      </TitleWrapper>
      <Typography variant="body2" color="textSecondary">
        {text.description}
      </Typography>
      <Divider />
      <Typography variant="h6">{text.form.general}</Typography>
      <TextField
        value={metadata?.title}
        variant="outlined"
        name="title"
        label={text.form.title}
        fullWidth
        required
        onChange={handleChange}
        margin="dense"
        multiline
        rows={2}
      />
      <Autocomplete
        multiple
        options={vocab}
        value={metadata?.tags || []}
        onChange={(_, value) =>
          setMetadata((prev) => ({ ...prev, tags: value.slice(0, MAX_TAGS) }))
        }
        // Block a 6th pick (removal still works); the slice above is a belt-and-braces cap.
        getOptionDisabled={() => (metadata?.tags?.length || 0) >= MAX_TAGS}
        filterOptions={tagFilter}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            label={text.form.tags}
            placeholder={
              (metadata?.tags?.length || 0) >= MAX_TAGS
                ? ""
                : text.form.tagsPlaceholder
            }
            margin="dense"
            helperText={`${metadata?.tags?.length || 0}/${MAX_TAGS}`}
          />
        )}
      />
      <Typography variant="h6">{text.form.preview}</Typography>
      <TextField
        value={metadata?.metadata?.banner}
        variant="outlined"
        label={text.form.banner}
        fullWidth
        required
        margin="dense"
        name="banner"
        onChange={handleChange}
      />
      <Image src={metadata?.metadata?.banner} alt="No Image Found" />
      <TextField
        value={metadata?.metadata?.description}
        variant="outlined"
        label={text.form.description}
        name="description"
        fullWidth
        required
        margin="dense"
        onChange={handleChange}
        multiline
        rows={5}
      />
    </DrawerWrapper>
  );
}
